import pclnExperimentation from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import { SegmentedChipGroup } from '@pcln/horizon'
import type { HotelFormState, LocationSearchType, MultiHotelFormState } from '@pcln/horizon-search-form'
import { HotelSearchForm, MultiHotelSearchForm } from '@pcln/horizon-search-form'
import { useCallback, useContext, useMemo, useRef, useState, type SetStateAction } from 'react'
import Sticky from 'react-stickynode'

import StickyEffect from '@/components/StickyEffect'
import fireMaxRoomsEvent from '@/components/TravelerSelection/ga4'
import { useHotelNeighbourhoodNesting } from '@/components/TypeAhead/useHotelNeighbourhoodNesting'
import { SearchFormContext } from '@/context/SearchFormContext'
import type { GA4PageNameType } from '@/ga4types'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import { fireBundleAndSaveEvent, fireMultiHotelRadioClick } from '../ga4'
import { handleSubmitForm as handleMultiHotelSubmitForm } from '../SearchForm/MultiHotelSearchForm/utils'
import { groupDealsURL } from '../SearchForm/utils'
import type {
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
  SelectedTopFilters,
  SEM_SEARCH_TYPE,
} from '../types'
import { resolveAiQuery } from './aiSearch'
import { AI_SEARCH_MODE, SEARCH_MODE_OPTIONS, STANDARD_SEARCH_MODE } from './aiSearchConstants'
import { AiSearchForm, type AiSearchSubmitParams } from './AiSearchForm'
import FormTypeRadios from './HorizonFormTypeRadio'
import HorizonSelectedFilterChipBar from './HorizonSelectedFilterChipBar'
import {
  logAiSearchModeToggle,
  logAiSearchNoRecommendationsSwitch,
  logAiSearchResolveFailed,
} from './logAiSearch'
import useHotelFormInitialValues from './useHotelFormInitialValues'

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) => void
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  showHotelTypeButtons?: boolean
  pageName?: 'homepage' | 'landing'
  isExpressDeal?: boolean
}

// Desktop-only experiment: prefill (pre-select) the destination field with the
// user's last searched city instead of leaving it empty. Only the single-hotel
// form's destination ("Where to?") field is in scope — its searchKey is the
// bare 'endLocation'. The multi-hotel form's per-destination fields
// (e.g. 'hotels.0.endLocation') are deliberately excluded: prefilling every
// empty destination with the same last-searched city would let a multi-hotel
// search submit duplicate destinations.
export function getEnablePrefillFromLastSearch({
  searchKey,
  isGreyTextVariant,
  isMobile,
}: {
  searchKey: string
  isGreyTextVariant: boolean
  isMobile: boolean
}) {
  return searchKey === 'endLocation' && isGreyTextVariant && !isMobile
}

// Switching to multi-hotel mode carries the single-hotel form's current
// destination into hotel #1's initial value (multiHotelInitialData below) —
// correct when the user actually chose that destination, but wrong when it
// only got there via the passive prefill: that would silently reproduce the
// "prefill every destination" problem multi-hotel is meant to be excluded
// from, just one hop removed (through the single-hotel form instead of
// AutocompleteWrapper's own prefill effect).
export function resolveMultiHotelInitialEndLocation<T>({
  endLocation,
  endLocationWasPrefilled,
}: {
  endLocation: T | null | undefined
  endLocationWasPrefilled: boolean
}): T | null {
  return endLocationWasPrefilled ? null : (endLocation ?? null)
}

export default function HorizonHotelForm({
  onSubmit,
  setLocation = () => null,
  showHotelTypeButtons = false,
  pageName = 'homepage',
  isExpressDeal = false,
}: FormType) {
  const { initialValues, isMobile, cguid, SEMSearch, SEMSEARCH, refId, refClickId } =
    useHotelFormInitialValues()
  const { updateFormValues } = useContext(SearchFormContext)
  const useV2Typeahead = useFeature('siteExperience.useV2Typeahead') ?? false
  const typeAheadEndpointHotels = useV2Typeahead ? 'hotelsV2' : 'hotels'

  const [hotelType, setHotelType] = useState('single')
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const [hotelTripType, setHotelTripType] = useState('STAY')
  const [searchMode, setSearchMode] = useState<string>(STANDARD_SEARCH_MODE)
  const isHomepage = pageName === 'homepage'
  const isMultiHotel = hotelType === 'multi' && showHotelTypeButtons
  // WEB_HTL_SEARCH_SCREEN_NBH_NESTING — the hook owns the VARIANT check, the hotelsV2 requirement
  // and the desktop-only gate. Multi-hotel always opts in; single-hotel only for a plain stay.
  const neighbourhoodNestingEnabled = useHotelNeighbourhoodNesting({
    typeAheadEndpoint: typeAheadEndpointHotels,
    enabled: isMultiHotel || hotelTripType === 'STAY',
  })

  const aiSearchModeVariant = useSeti({
    experimentCode: 'AI_SEARCH_IN_YOUR_OWN_WORDS',
    shouldFireImpression: isHomepage,
  })
  const showSearchModeChips = isHomepage && aiSearchModeVariant === 'VARIANT'
  const isAiSearchMode = showSearchModeChips && searchMode === AI_SEARCH_MODE

  const enableStickyEffect =
    !isExpressDeal && !isMobile && pageName === 'homepage' && hotelType === 'single' && !isAiSearchMode

  const isAiSearchEnabled = !isExpressDeal && isMobile && hotelType === 'single' && hotelTripType === 'STAY'

  const aiSearchVariant = useSeti({
    experimentCode: 'USB_SEARCH_AI_QUERIES',
    shouldFireImpression: isAiSearchEnabled,
  })

  const isLTRModelEnabled = !isExpressDeal && !isMobile && hotelTripType === 'STAY'

  const ltrModelVariant = useSeti({
    experimentCode: 'USB_SEARCH_LTR_MODEL',
    shouldFireImpression: isLTRModelEnabled,
  })

  const isMwebLTRModelEnabled = !isExpressDeal && isMobile && hotelTripType === 'STAY'

  const mwebLtrModelVariant = useSeti({
    experimentCode: 'USB_SEARCH_MWEB_LTR',
    shouldFireImpression: isMwebLTRModelEnabled,
  })

  const hasLTRSearch =
    (ltrModelVariant === 'VARIANT' && isLTRModelEnabled) ||
    (mwebLtrModelVariant === 'VARIANT' && isMwebLTRModelEnabled)

  const hasAISearch =
    (aiSearchVariant === 'VARIANT_1' || aiSearchVariant === 'VARIANT_2') && isAiSearchEnabled

  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      if (enableStickyEffect) {
        setIsStickyInEffect(true)
      }
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }

  const isPartnersURL =
    useSeti({
      experimentCode: 'HP_LP_HOTELS_TEN_ROOMS',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const removeBackground =
    useSeti({
      experimentCode: 'HP_SEARCH_FORM_HORIZON_CROSS_SELL_DESKTOP',
      shouldFireImpression: !isExpressDeal,
    }) === 'VARIANT'

  const isPackageCheckboxHighlightEnabled =
    useSeti({
      experimentCode: 'PKG_MWEB_CHECKBOX_HIGHLIGHT',
      shouldFireImpression: isMobile && !isExpressDeal,
    }) === 'VARIANT' && isMobile

  const aiSearchButtonVariant = useSeti({
    experimentCode: 'USB_AI_SEARCH_BUTTON_HP',
    shouldFireImpression: isHomepage,
  })
  const showAiSearchButton =
    isHomepage && (aiSearchButtonVariant === 'VARIANT1' || aiSearchButtonVariant === 'VARIANT2')

  const recentSearchImprovementVariant = useSeti({
    experimentCode: 'HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV',
    shouldFireImpression: false,
  })
  const mergeRecentAndRecommendedSearches = recentSearchImprovementVariant === 'VARIANT1'
  const preferRecentSearchesOnly = recentSearchImprovementVariant === 'VARIANT2'

  // Desktop-only: prefill the "Where to?" field with the user's last searched city.
  const isGreyTextVariant =
    useSeti({
      experimentCode: 'HP_HOTEL_SEARCH_GREY_TEXT',
      shouldFireImpression: !isExpressDeal && !isMobile,
    }) === 'VARIANT'

  const fireRecentSearchImprovementImpression = useCallback(() => {
    pclnExperimentation.fireImpression('HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV')
  }, [])

  // Captures the typeahead `select_content` item_name for the selected destination
  // so the submit `search` event can carry the same value.
  const searchItemNameRef = useRef<string | null>(null)

  // Tracks whether trackedValues.endLocation currently holds a passive prefill
  // rather than a real user pick, so multiHotelInitialData (below) can exclude
  // it — otherwise switching to multi-hotel mode would carry the single-hotel
  // form's auto-prefilled destination into hotel #1 as if the user had chosen
  // it themselves, which is exactly the "prefill every destination" problem
  // the multi-hotel form is supposed to be excluded from.
  const [endLocationWasPrefilled, setEndLocationWasPrefilled] = useState(false)

  const [trackedValues, setTrackedValues] = useState<Partial<HotelFormState>>({
    endLocation: initialValues.endLocation as LocationSearchType | null,
    startDate: initialValues.startDate,
    endDate: initialValues.endDate,
    travelers: initialValues.travelers,
  })

  const hotelPlannerLink = groupDealsURL({
    location: trackedValues.endLocation?.itemName ?? '',
    startDate: trackedValues.startDate ?? '',
    endDate: trackedValues.endDate ?? '',
    isPartnersURL,
  })

  const multiHotelInitialData = useMemo(
    () => ({
      endLocation: resolveMultiHotelInitialEndLocation({
        endLocation: trackedValues.endLocation,
        endLocationWasPrefilled,
      }),
      startDate: trackedValues.startDate ?? '',
      endDate: trackedValues.endDate ?? '',
      travelers: trackedValues.travelers ?? initialValues.travelers,
    }),
    [
      endLocationWasPrefilled,
      trackedValues.endLocation,
      trackedValues.startDate,
      trackedValues.endDate,
      trackedValues.travelers,
      initialValues.travelers,
    ],
  )

  const handleFormValuesChange = useCallback(
    (values: HotelFormState) => {
      setTrackedValues(values)
      updateFormValues?.(values as unknown as HOTEL_FORM_STATE_TYPE)
    },
    [updateFormValues],
  )

  const handleSubmit = useCallback(
    (data: HotelFormState) => {
      if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
        if (isHomepage) {
          window.PCLN.pclnEventDispatcher.dispatch('FIND YOUR HOTEL')
        }
        window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_HOTEL_DATA', {
          cityName: data.endLocation?.cityName,
          cityId: data.endLocation?.cityID,
          checkInDate: data.startDate,
          checkOutDate: data.endDate,
        })
      }

      let userLatitude: number | undefined
      let userLongitude: number | undefined
      if (data.endLocation?.type === 'GEO' && data.endLocation?.lat && data.endLocation?.lon) {
        userLatitude = data.endLocation.lat
        userLongitude = data.endLocation.lon
      }

      const formValues = {
        ...data,
        ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
        ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
        ...(refId && { refId }),
        ...(refClickId && { refClickId }),
        useCurrentLocation: data.endLocation?.type === 'GEO',
        ...(userLatitude && userLongitude && { userLatitude, userLongitude }),
        ...(searchItemNameRef.current ? { searchItemName: searchItemNameRef.current } : {}),
      }

      onSubmit(formValues as unknown as HOTEL_FORM_SUBMIT_TYPE, pageName)
      logSearchFormAnalytics({
        cguid,
        pageName,
        productType: 'hotels',
        isMobile,
      })
    },
    [isMobile, isHomepage, SEMSearch, SEMSEARCH, refId, refClickId, onSubmit, pageName, cguid],
  )

  const handleMultiHotelSubmit = useCallback(
    (data: MultiHotelFormState) => {
      handleMultiHotelSubmitForm(data)
      logSearchFormAnalytics({
        cguid,
        pageName,
        productType: 'multi-hotel',
        isMobile,
      })
    },
    [cguid, isMobile, pageName],
  )

  const handleAiSearchSubmit = useCallback(
    async ({ query, startDate, endDate, travelers, resolvedLocation }: AiSearchSubmitParams) => {
      const endLocation = resolvedLocation ?? (await resolveAiQuery({ query }))
      if (!endLocation) {
        logAiSearchResolveFailed(query)
        throw new Error('AI_SEARCH_NO_MATCH')
      }

      // Delegate to the standard submit path (SEM params, GEO lat/lon, onSubmit, analytics)
      // instead of re-deriving it — same URL-building logic as the standard hotel form.
      handleSubmit({
        ...initialValues,
        endLocation,
        startDate,
        endDate,
        travelers,
      } as unknown as HotelFormState)
    },
    [handleSubmit, initialValues],
  )

  const renderStandardForm = () => (
    <>
      {showHotelTypeButtons && (
        <FormTypeRadios
          formType={hotelType}
          onChange={(selectedFormName) => {
            if (hasAISearch && selectedFormName === 'multi') {
              setTrackedValues((prev) => ({ ...prev, endLocation: null }))
            }
            setHotelType(selectedFormName)
            fireMultiHotelRadioClick(pageName)
          }}
        />
      )}
      <StickyEffect
        enabled={enableStickyEffect}
        isStickyInEffect={isStickyInEffect}
        handleStickyEffectStateChange={handleStickyEffectStateChange}
        innerZ={isMobile ? 0 : 3}
      >
        {isMultiHotel ? (
          <MultiHotelSearchForm
            initialData={multiHotelInitialData}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            onSubmit={handleMultiHotelSubmit}
            autocompleteOptions={({ searchKey }) => ({
              isMobile,
              mergeRecentAndRecommendedSearches,
              preferRecentSearchesOnly,
              onRecentSearchImprovementImpression: fireRecentSearchImprovementImpression,
              neighbourhoodNestingEnabled,
              hasLTRSearch,
              enablePrefillFromLastSearch: getEnablePrefillFromLastSearch({
                searchKey,
                isGreyTextVariant,
                isMobile,
              }),
            })}
          />
        ) : (
          <HotelSearchForm
            initialValues={initialValues as unknown as Partial<HotelFormState>}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            setLocation={setLocation}
            isStickyInEffect={isStickyInEffect}
            hotelPlannerLinkHref={hotelPlannerLink}
            removeBackground={isExpressDeal || removeBackground}
            highlightBundleAndSave={isPackageCheckboxHighlightEnabled}
            showBundleAndSave={!isExpressDeal}
            showCurrentLocation={!isExpressDeal}
            onMaxRoomsClick={() => {
              pclnExperimentation.fireImpression('HP_LP_HOTELS_TEN_ROOMS')
              window.open(hotelPlannerLink, '_blank')
              fireMaxRoomsEvent(pageName, 'hotel')
            }}
            onBundleChange={(tripType, checked) => {
              setHotelTripType(tripType)
              if (checked) {
                try {
                  fireBundleAndSaveEvent(tripType)
                } catch {
                  // GA4TripTypeMapper may throw on unexpected trip type combinations
                }
              }
            }}
            onFormValuesChange={handleFormValuesChange}
            onSubmit={handleSubmit}
            submitButtonFixedOnMobile={!isExpressDeal}
            autocompleteOptions={({ searchKey }) => ({
              isMobile,
              showFilterChips: !isExpressDeal,
              aiSearchButtonVariant: showAiSearchButton ? aiSearchButtonVariant : undefined,
              mergeRecentAndRecommendedSearches,
              preferRecentSearchesOnly,
              onRecentSearchImprovementImpression: fireRecentSearchImprovementImpression,
              neighbourhoodNestingEnabled,
              hasAISearch,
              hasLTRSearch,
              enablePrefillFromLastSearch: getEnablePrefillFromLastSearch({
                searchKey,
                isGreyTextVariant,
                isMobile,
              }),
              // Only the destination's selection feeds the submit `search` event's item_name; the
              // Bundle & Save departure airport shares this form but not that analytics field.
              onSelectItemName:
                searchKey === 'endLocation'
                  ? (name) => {
                      searchItemNameRef.current = name
                    }
                  : undefined,
            })}
            onLocationSelect={(searchKey, _item, context) => {
              // A real pick on the destination supersedes an earlier prefill; picking the
              // departure airport must not clear the flag while the destination is still prefilled.
              if (searchKey === 'endLocation') {
                setEndLocationWasPrefilled(Boolean(context?.isPrefill))
              }
            }}
            renderSelectedFilterChipBar={
              isExpressDeal
                ? undefined
                : (chipBarProps) => (
                    <HorizonSelectedFilterChipBar
                      selectedFilterChipData={
                        chipBarProps.selectedFilterChipData as unknown as SelectedTopFilters
                      }
                      setSelectedFilterChipData={
                        chipBarProps.setSelectedFilterChipData as unknown as (
                          value: SetStateAction<SelectedTopFilters>,
                        ) => void
                      }
                    />
                  )
            }
          />
        )}
      </StickyEffect>
    </>
  )

  if (showSearchModeChips) {
    // Mobile drops the leading icons so the label and Beta badge fit the full-width track.
    const searchModeOptions = isMobile
      ? SEARCH_MODE_OPTIONS.map((option) => ({ ...option, iconLeft: undefined, customIcon: undefined }))
      : SEARCH_MODE_OPTIONS

    return (
      <div className='@container flex w-auto flex-col gap-3'>
        <div className={isMobile ? 'w-full' : undefined}>
          <SegmentedChipGroup
            name='search-mode'
            options={searchModeOptions}
            value={searchMode}
            handleChange={(value: unknown) => {
              const nextMode = String(value)
              if (nextMode === searchMode) return
              logAiSearchModeToggle(nextMode)
              setSearchMode(nextMode)
            }}
            aria-label='Search mode'
            fullWidth={isMobile}
          />
        </div>
        {isAiSearchMode ? (
          <AiSearchForm
            isMobile={isMobile}
            initialStartDate={initialValues.startDate}
            initialEndDate={initialValues.endDate}
            initialTravelers={initialValues.travelers}
            onSubmitQuery={handleAiSearchSubmit}
            onSwitchToStandardSearch={(searchQuery) => {
              logAiSearchNoRecommendationsSwitch(searchQuery)
              setSearchMode(STANDARD_SEARCH_MODE)
            }}
          />
        ) : (
          renderStandardForm()
        )}
      </div>
    )
  }

  return <div className='flex w-auto flex-col gap-3'>{renderStandardForm()}</div>
}
